'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HELP_CATEGORIES, HELP_FEATURES, type HelpFeature } from './helpData';

export const ALL_CATEGORY = 'all';
export const SHORTCUTS_CATEGORY = '__shortcuts';

export interface HelpCategorySummary {
  id: string;
  label: string;
  count: number;
}

/** Resolved display text of one feature in the active language. */
export interface HelpFeatureText {
  name: string;
  description: string;
  uiPath: string;
}

/**
 * Feature text for the active language: the locale's `helpCenter.entries.<id>`
 * translation when it carries one, else the English dataset text from
 * `helpData.ts` via `defaultValue`.
 */
export function useHelpText(): (feature: HelpFeature) => HelpFeatureText {
  const { t } = useTranslation();
  return useCallback(
    (feature: HelpFeature) => ({
      name: t(`helpCenter.entries.${feature.id}.name`, { defaultValue: feature.name }),
      description: t(`helpCenter.entries.${feature.id}.description`, { defaultValue: feature.description }),
      uiPath: t(`helpCenter.entries.${feature.id}.uiPath`, { defaultValue: feature.uiPath }),
    }),
    [t],
  );
}

/**
 * Category label for the active language: the locale's `helpCenter.categories`
 * line when present, else the dataset's English category id.
 */
export function useCategoryLabel(): (category: string) => string {
  const { t } = useTranslation();
  return useCallback(
    (category: string) => t(`helpCenter.categories.${category}`, { defaultValue: category }),
    [t],
  );
}

/**
 * Searchable text covers the English dataset words and the active language's
 * words, so a query matches in either language.
 */
function buildSearchText(feature: HelpFeature, text: HelpFeatureText): string {
  return [
    feature.id,
    feature.name,
    text.name,
    feature.description,
    text.description,
    feature.category,
    feature.endpoint ?? '',
    feature.uiPath,
    text.uiPath,
    feature.scope,
    feature.badge,
    feature.shortcut ?? '',
    ...feature.trigger,
    ...feature.roles,
    ...feature.keywords,
  ]
    .join(' ')
    .toLowerCase();
}

/**
 * Data provider for the Help Center: holds the master help dataset,
 * the active search term and the selected category, and derives the
 * filtered feature lists from them (no synchronized state).
 */
export function useHelpData() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>(ALL_CATEGORY);
  const { t } = useTranslation();
  const entryText = useHelpText();
  const categoryLabel = useCategoryLabel();

  // Rebuilt when the language changes, so a query matches the shown words.
  const searchIndex = useMemo(
    () => new Map(HELP_FEATURES.map((feature) => [feature.id, buildSearchText(feature, entryText(feature))])),
    [entryText],
  );

  const matchesQuery = useCallback(
    (feature: HelpFeature) => {
      const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
      if (tokens.length === 0) return true;
      const haystack = searchIndex.get(feature.id) ?? buildSearchText(feature, entryText(feature));
      return tokens.every((token) => haystack.includes(token));
    },
    [query, searchIndex, entryText],
  );

  const categories = useMemo<HelpCategorySummary[]>(() => {
    const counts = new Map<string, number>();
    HELP_FEATURES.forEach((feature) => {
      counts.set(feature.category, (counts.get(feature.category) ?? 0) + 1);
    });

    const browse: HelpCategorySummary[] = [
      { id: ALL_CATEGORY, label: t('helpCenter.chrome.allFeatures'), count: HELP_FEATURES.length },
      ...HELP_CATEGORIES.map((id) => ({ id, label: categoryLabel(id), count: counts.get(id) ?? 0 })),
    ];

    return [...browse, { id: SHORTCUTS_CATEGORY, label: t('helpCenter.chrome.shortcutsCategory'), count: HELP_FEATURES.length }];
  }, [categoryLabel, t]);

  const features = useMemo(() => {
    const scoped =
      category === ALL_CATEGORY || category === SHORTCUTS_CATEGORY
        ? HELP_FEATURES
        : HELP_FEATURES.filter((feature) => feature.category === category);
    return scoped
      .filter(matchesQuery)
      .sort((a, b) => entryText(a).name.localeCompare(entryText(b).name));
  }, [category, matchesQuery, entryText]);

  const matrixFeatures = useMemo(() => HELP_FEATURES.filter(matchesQuery), [matchesQuery]);

  const clearSearch = useCallback(() => setQuery(''), []);

  return {
    query,
    setQuery,
    category,
    setCategory,
    categories,
    features,
    matrixFeatures,
    totalCount: HELP_FEATURES.length,
    resultCount: features.length,
    clearSearch,
    /** Display text resolver in the active language (dataset English fallback). */
    entryText,
    /** Display label resolver for category ids in the active language. */
    categoryLabel,
  };
}
